<template>
  <InputWrapper
    v-if="options"
    v-model="model"
    :className="`input-row--select ${className}`"
    :name="name"
    :error="error"
    :label="label"
    :hasIconLeft="hasIconLeft"
    :placeholder="placeholder"
    :disabled="disabled ?? false"
  >
    <template #icon>
      <slot name="icon"></slot>
    </template>

    <template #input>
      <div ref="input-select">
        <div
          :name="name"
          class="form-input form-input--select"
          @click="toggleExpansion"
          tabindex="0"
          @keydown.enter="onEnterPressed"
        >
          <slot name="displayText">{{ displayText }}</slot>
          <IconCaretDown
            class="form-input--select-arrow"
            :class="{ 'form-input--select-arrow-up': isExpanded }"
          />
        </div>

        <Teleport to="#select-anchor">
          <div
            class="form-input--select-dropdown-container"
            ref="options-container"
            tabindex="0"
            v-show="isExpanded"
          >
            <ul
              tabindex="0"
              ref="select-options"
              role="listbox"
              class="form-input--select-dropdown"
              :style="calculatedStyle"
              @keydown.tab="updateTab"
              @keydown.up="arrowUp"
              @keydown.down="arrowDown"
              @keydown.escape="onEscapePressed"
            >
              <li v-show="filter" tabindex="0" class="form-input--select-filter">
                <InputText v-model="search" ref="input-filter" name="filter" :hasIconRight="true">
                  <template #icon-right>
                    <IconSearch />
                  </template>
                </InputText>
              </li>
              <li
                role="option"
                tabindex="0"
                :class="{ 'form-input--option-active': getOptionValue(option) === model }"
                v-for="option in availableOptions"
                :key="getOptionValue(option)"
                @keydown.enter="selectOption(getOptionValue(option))"
                @click="selectOption(getOptionValue(option))"
              >
                <slot name="optionText" v-bind="option">
                  {{ getOptionLabel(option) }}
                </slot>
              </li>
            </ul>
          </div>
        </Teleport>
      </div>
    </template>
  </InputWrapper>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import type { FocusTrap } from 'focus-trap';
import { createFocusTrap } from 'focus-trap';

import InputWrapper from './InputWrapper.vue';
import InputText from './InputText.vue';

import IconCaretDown from '@/icons/arrows/IconCaretDown.svg?component';
import IconSearch from '@/icons/IconSearch.svg?component';

const model = defineModel();
const emit = defineEmits(['optionSelected']);

const inputSelect = useTemplateRef('input-select');
const inputFilter = useTemplateRef('input-filter');
const optionsContainer = useTemplateRef('options-container');
const selectOptions = useTemplateRef('select-options');

const calculatedStyle = ref({});

const search = ref('');

let trap: FocusTrap | undefined = undefined;

type optionType = {
  value?: string | number;
  id?: string | number | null;
  label?: string;
  display_name?: string;
  sso_enabled?: boolean;
};

const props = withDefaults(
  defineProps<{
    label?: string; // top, above
    labelPos?: string;
    disabled?: boolean | null;
    name?: string;
    error?: string;
    placeholder?: string;
    options: optionType[] | string[] | any[] | null;
    optionLabel?: string;
    optionValue?: string;
    filter?: boolean;
    className?: string;
    hasIconLeft?: boolean;
  }>(),
  {
    label: '',
    labelPos: '',
    disabled: null,
    name: '',
    error: '',
    placeholder: '',
    options: null,
    filter: false,
    className: '',
    hasIconLeft: false,
  },
);

const isExpanded = ref(false);

const outsideClickListener = (event: Event) => {
  if (!inputSelect.value?.contains(event.target as HTMLInputElement)) {
    closeDropdown();
  }
};

const openDropdown = () => {
  calculateSizePosition();
  isExpanded.value = true;
  document.addEventListener('click', outsideClickListener, true);
  document.addEventListener('scroll', calculateSizePosition, true);

  nextTick(() => {
    // Set up Focus Trap after elements are visible because otherwise it throws fits.
    const inputDiv = optionsContainer.value as HTMLElement;
    trap = createFocusTrap(inputDiv, {});
    trap.activate();
    highlightIndex.value = -1;

    // If we are using Filter, autofocus the input when we open
    // the dropdown menu.
    if (props.filter) {
      if (inputFilter.value?.inputText) {
        inputFilter.value.inputText.focus();
      }
    }
  });
};

const closeDropdown = () => {
  isExpanded.value = false;
  document.removeEventListener('click', outsideClickListener, true);
  document.removeEventListener('scroll', calculateSizePosition, true);
  trap?.deactivate();
};

const toggleExpansion = () => {
  if (!isExpanded.value && !props.disabled) {
    openDropdown();
  } else {
    closeDropdown();
  }
};

const onEnterPressed = (e: Event) => {
  const target = e.target as HTMLElement;
  if (!isExpanded.value && target.tagName != 'LI') {
    openDropdown();
  } else {
    closeDropdown();
  }
};

const onEscapePressed = (e: Event) => {
  const target = e.target as HTMLElement;
  if (!isExpanded.value && target.tagName != 'LI') {
    openDropdown();
  } else {
    closeDropdown();
  }
};

const prevIndex = () => {
  if (highlightIndex.value <= 0) {
    highlightIndex.value = (props.options?.length || 0) - 1;
  } else {
    highlightIndex.value = highlightIndex.value - 1;
  }
};

const arrowUp = (e: KeyboardEvent) => {
  e.preventDefault();
  prevIndex();
  if (listItems.value) {
    listItems.value[highlightIndex.value].focus();
  }
};

const nextIndex = () => {
  if (highlightIndex.value === (props.options?.length || 0) - 1) {
    highlightIndex.value = 0;
  } else {
    highlightIndex.value = highlightIndex.value + 1;
  }
};

const arrowDown = (e: KeyboardEvent) => {
  e.preventDefault();
  nextIndex();
  if (listItems.value) {
    listItems.value[highlightIndex.value].focus();
  }
};

const updateTab = (e: KeyboardEvent) => {
  if (e.shiftKey) {
    prevIndex();
  } else {
    nextIndex();
  }
};

const displayText = computed(() => {
  if (model.value) {
    return getOptionLabel(model.value);
  } else if (props.placeholder) {
    return props.placeholder;
  }
  return '';
});

const getOptionValue = (option: any) => {
  if (props.optionValue) {
    return option[props.optionValue];
  } else if (typeof option === 'object') {
    if (option.id) {
      return option.id;
    }
    if (option.value) {
      return option.value;
    }
  }
  return option;
};

const getOptionLabel = (option: any) => {
  if (!option) {
    return '';
  }

  if (props.optionLabel && typeof option === 'object') {
    return option[props.optionLabel];
  }

  if (props.optionLabel) {
    const fullOption = props.options?.find((listOption) => {
      return listOption[props.optionValue as string] === option;
    });
    if (fullOption) {
      return fullOption[props.optionLabel];
    }
  }

  return option;
};

const selectOption = (option: optionType | string) => {
  model.value = option;
  emit('optionSelected', option);
  closeDropdown();
};

const highlightIndex = ref(0);

const listItems = computed(() => {
  return selectOptions.value?.getElementsByTagName('li');
});

const calculateSizePosition = () => {
  const inputPos = inputSelect.value?.getBoundingClientRect();

  if (inputPos) {
    const spaceAbove = inputPos.top || 0;
    const spaceBelow = window.innerHeight - inputPos.bottom;

    let left = inputPos.left;
    if (props.hasIconLeft) {
      left = inputPos.left - 32;
    }

    if (spaceAbove > spaceBelow) {
      let heightCap = spaceAbove - inputPos.height;
      if (heightCap > 280) {
        heightCap = 280;
      }

      calculatedStyle.value = {
        bottom: -inputPos.bottom + 40 + 'px',
        maxHeight: heightCap + 'px',
        left: left + 'px',
        width: inputPos.width + 'px',
      };
    } else {
      let heightCap = spaceBelow - inputPos.height;
      if (heightCap > 280) {
        heightCap = 280;
      }

      calculatedStyle.value = {
        top: inputPos.top + 40 + 'px',
        maxHeight: heightCap + 'px',
        left: left + 'px',
        width: inputPos.width + 'px',
      };
    }
  }
};

const availableOptions = computed(() => {
  if (search.value != '' && props.options) {
    const filteredOptions = [...props.options].filter((option) => {
      return getOptionLabel(option).toLocaleLowerCase().includes(search.value.toLocaleLowerCase());
    });
    return filteredOptions;
  }

  return props.options;
});
</script>
